// @erp/module-cms/schemas — the per-content-TYPE category taxonomy wire
// contracts. Browser-safe: zod + plain data only (fe forms and the gateway parse
// the SAME schemas, so the admin UI and the server can never disagree on what a
// valid category is). Mirrors the modules/lib catalogue registry contracts
// (Subject/Tag) — a colour, a slug, a create/update/DTO/list-query family.
//
// Scope (2026-08-19 product decision): the CMS is the DYNAMIC-CONTENT backend for
// a STATIC site, locale-ready but English-first. Categories are TYPED by content
// kind — the six decided types below — and slug'd per (type, locale). The MBSPSU
// per-column translation shape (nameHi/namePa/descriptionHi …) does NOT port:
// a translation is a SIBLING ROW under a different `locale` (db-cms 0002 comment),
// so this lane stays flat and English-first while the schema is i18n-ready.
import { z } from "zod";
import { CategoryHue, EntityId, EpochMs } from "@erp/core-schemas";
import { Locale } from "./locale.js";
import { IconToken } from "./structured.js";

// ── The content-type vocabulary (the decided kinds — a bad type 400s) ──────────
export const CMS_CATEGORY_TYPES = [
  "announcement",
  "publication",
  "event",
  "tender",
  "gallery",
  "newsletter",
] as const;
export const CmsCategoryType = z.enum(CMS_CATEGORY_TYPES);
export type CmsCategoryType = z.infer<typeof CmsCategoryType>;

// ── slug grammar (lower-kebab) + the deriver ───────────────────────────────────

/** Lower-kebab: `a-z0-9` runs joined by single hyphens, no leading/trailing hyphen. Exported so sibling content slices (announcements …) reuse the ONE slug grammar. */
export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Max slug length — the DERIVED slug is bounded to the SAME max an explicit slug must satisfy. */
export const SLUG_MAX = 120;

/**
 * Derive a lower-kebab slug from free text — the create path uses this when no
 * explicit slug is sent (English-first; NFKD strips accents to their base letter,
 * then every non-`a-z0-9` run collapses to a hyphen). BOUNDED to SLUG_MAX so the
 * derived path can never exceed the explicit-slug limit; a truncation that lands
 * mid-run re-trims its trailing hyphen. A symbol/non-latin-only name yields "" —
 * the caller (repo.create) supplies a non-empty fallback (the minted id).
 */
export const slugify = (raw: string): string =>
  raw
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX)
    .replace(/-+$/g, "");

// ── locale grammar — the ONE rule every `locale` column and ?locale filter obeys ─────
// `Locale` lives in ./locale.ts since the locale contract (2026-09-14): a canonical BCP 47
// tag with a registered language, canonicalised on parse. Imported here for the category
// rows' own `locale` column; the content schemas import it from there too.

// ── The write-side field validators (one source, Create/Update both derive) ────
// The colour is a categorization hue NAME since D2 / db-cms 0021 (2026-09-19) — the
// 6-digit HEX_COLOR grammar is retired; the admin picks from HuePicker and the
// chips paint the name two-tone in both themes.
const categoryCore = {
  type: CmsCategoryType,
  name: z.string().trim().min(1).max(200),
  slug: z.string().trim().min(1).max(SLUG_MAX).regex(SLUG_RE, "Slug must be lower-kebab (a-z, 0-9, hyphen)"),
  /** One of the nine hue names; nullable so a PATCH can clear it back to no-colour. */
  color: CategoryHue.nullable(),
  description: z.string().trim().max(2000).nullable(),
  /** The heroicon NAME Django keeps beside the colour (0018, A5) — a slug-like token (schemas/structured.ts IconToken), never markup. Nullable to clear. */
  icon: IconToken.nullable(),
  sortOrder: z.number().int().min(0),
  /** The row's locale — a canonical BCP 47 tag (./locale.ts); the DDL's authored default is 'en' (db-cms 0002), the router substitutes the install's DEFAULT_LOCALE for an absent value. */
  locale: Locale,
};

/**
 * Create body — type + name required; slug OPTIONAL (the service derives it via
 * slugify(name) when absent). sortOrder/locale carry the DDL defaults.
 */
export const CreateCmsCategory = z.object({
  type: categoryCore.type,
  name: categoryCore.name,
  slug: categoryCore.slug.optional(),
  color: categoryCore.color.optional(),
  description: categoryCore.description.optional(),
  icon: categoryCore.icon.optional(),
  sortOrder: categoryCore.sortOrder.default(0),
  locale: categoryCore.locale.default("en"),
});
export type CreateCmsCategory = z.infer<typeof CreateCmsCategory>;

/** Update body — a partial PATCH over the same fields (slug collisions still map to 409). */
export const UpdateCmsCategory = z.object(categoryCore).partial();
export type UpdateCmsCategory = z.infer<typeof UpdateCmsCategory>;

/** List query — optional `?type` and `?locale` filters (both exact-eq; each omitted lists every value). ?locale (0018) lets the admin list show one locale's sibling rows. */
export const CmsCategoryListQuery = z.object({ type: CmsCategoryType.optional(), locale: Locale.optional() });
export type CmsCategoryListQuery = z.infer<typeof CmsCategoryListQuery>;

/** The wire DTO — the read shape (camelCase; audit actor columns stay server-side). */
export const CmsCategory = z.object({
  id: EntityId,
  type: CmsCategoryType,
  name: z.string(),
  slug: z.string(),
  /** A hue name (0021) or null. */
  color: CategoryHue.nullable(),
  description: z.string().nullable(),
  /** 0018 — the icon token or null. */
  icon: z.string().nullable(),
  sortOrder: z.number().int(),
  locale: z.string(),
  createdAt: EpochMs,
  updatedAt: EpochMs,
});
export type CmsCategory = z.infer<typeof CmsCategory>;
